import { useState, useEffect, useCallback } from 'react';
import { useSessionState } from '../../hooks/useSessionState';
import { useNavigate } from 'react-router-dom';
import {
  Search, Edit, Trash2, ChevronLeft, ChevronRight, ShoppingCart, FilterX,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { api } from '../../core/apiClient';
import { ExcelFilterDropdown } from '../../components/ui/excel-filter';

interface SalesOrder {
  id: string;
  orderNumber: string;
  order_date: string;
  status: string;
  client_name: string;
  project_name?: string;
  project_number?: string;
  budget_number?: string;
  total: number;
}

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  draft:     { label: 'Esborrany',  className: 'bg-gray-100 text-gray-700' },
  accepted:  { label: 'Acceptat',   className: 'bg-blue-100 text-blue-700' },
  delivered: { label: 'Lliurat',    className: 'bg-green-100 text-green-700' },
  invoiced:  { label: 'Facturat',   className: 'bg-purple-100 text-purple-700' },
  cancelled: { label: 'Cancel·lat', className: 'bg-red-100 text-red-700' },
};

const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString('ca-ES') : '—');
const fmtCur = (v: number) =>
  Number(v || 0).toLocaleString('ca-ES', { style: 'currency', currency: 'EUR' });

export default function SalesOrderList() {
  const navigate = useNavigate();

  const [orders, setOrders]     = useState<SalesOrder[]>([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]             = useSessionState('sales:search', '');
  const [statusFilter, setStatusFilter] = useSessionState('sales:statusFilter', '');
  const [page, setPage]         = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [deleting, setDeleting] = useState<string | null>(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = { page, limit: 20 };
      if (search)      params.search = search;
      if (statusFilter) params.status = statusFilter;

      const res = await api.get<any>('/sales-orders', params);
      setOrders(res.data ?? []);
      setTotalPages(res.pagination?.totalPages ?? 1);
    } catch (err) {
      console.error('Error carregant comandes:', err);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleDelete = async (id: string, num: string) => {
    if (!confirm(`Eliminar la comanda ${num}?`)) return;
    setDeleting(id);
    try {
      await api.delete(`/sales-orders/${id}`);
      fetchOrders();
    } catch (err) {
      console.error('Error eliminant:', err);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4">
          <form
            onSubmit={(e) => { e.preventDefault(); setPage(1); fetchOrders(); }}
            className="flex flex-wrap gap-3"
          >
            <button
              type="button"
              onClick={() => { setSearch(''); setStatusFilter(''); setPage(1); }}
              title="Netejar tots els filtres"
              className="flex items-center justify-center px-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shrink-0"
            >
              <FilterX size={16} className="text-muted-foreground" />
            </button>
            <div className="relative flex-1 min-w-[200px]">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                autoFocus
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cercar per número, client..."
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <ExcelFilterDropdown
              label="Estat"
              options={Object.entries(STATUS_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))}
              selected={statusFilter ? statusFilter.split(',') : []}
              onApply={(values) => { setStatusFilter(values.join(',')); setPage(1); }}
            />
            <button
              type="submit"
              className="px-4 py-2 bg-gray-900 text-white rounded-lg text-xs hover:bg-gray-700 transition-colors"
            >
              Cercar
            </button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <ShoppingCart size={40} className="mb-3 opacity-30" />
              <p className="text-xs">No s'han trobat comandes de client</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Número</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Expedient</TableHead>
                  <TableHead>Pressupost</TableHead>
                  <TableHead>Estat</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="w-20"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((o) => {
                  const cfg = STATUS_CONFIG[o.status] ?? { label: o.status, className: 'bg-gray-100 text-gray-600' };
                  return (
                    <TableRow
                      key={o.id}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => navigate(`/crm/comandes/${o.id}/edit`)}
                    >
                      <TableCell className="font-medium">{o.orderNumber}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{fmtDate(o.order_date)}</TableCell>
                      <TableCell className="text-xs">{o.client_name ?? '—'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {o.project_number ?? (o.project_name ?? '—')}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{o.budget_number ?? '—'}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg.className}`}>
                          {cfg.label}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-medium">{fmtCur(o.total)}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            title="Editar"
                            onClick={() => navigate(`/crm/comandes/${o.id}/edit`)}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-colors"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            title="Eliminar"
                            onClick={() => handleDelete(o.id, o.orderNumber)}
                            disabled={deleting === o.id}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600 transition-colors disabled:opacity-40"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Pàgina {page} de {totalPages}</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
